/**
 * One tab per kept project: a tab holds a Web Lock for the project's
 * id while the project is open in it. Web Locks are shared by the tabs of one
 * origin in one browser profile, which is also what shares the kept projects,
 * and the browser releases a tab's locks when it closes or reloads.
 */

export interface ProjectLock {
    release(): void;
}

/**
 * Hold the lock of project `id`, or answer undefined when another tab holds
 * it. With `steal`, take it from that tab, whose `lost` is then called.
 * Where the browser has no Web Locks, every request is granted.
 */
export function holdProject(id: string, steal: boolean, lost: () => void): Promise<ProjectLock | undefined> {
    const locks = (navigator as { locks?: LockManager }).locks;
    if (locks === undefined) return Promise.resolve({ release: () => {} });
    return new Promise((resolve) => {
        let release = () => {};
        const held = new Promise<void>((done) => (release = done));
        const options: LockOptions = steal ? { steal: true } : { ifAvailable: true };
        locks
            .request(`idfweb-project:${id}`, options, (lock) => {
                if (lock === null) {
                    resolve(undefined);
                    return undefined;
                }
                resolve({ release });
                return held;
            })
            .catch((e: unknown) => {
                // Another tab stole it: the request settles with an AbortError.
                if (e instanceof DOMException && e.name === "AbortError") lost();
            });
    });
}
